import { useEffect, useRef, useState } from 'react';
import Lottie from 'lottie-react';
import AuthBackdrop from './AuthBackdrop';

const CheckIcon = () => (
  <span className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-white/15 border border-white/20 flex-shrink-0">
    <svg className="w-3 h-3 text-primary-100" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={3}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
    </svg>
  </span>
);

const BrandMark = () => (
  <div className="inline-flex items-center justify-center w-12 h-12 bg-white/15 backdrop-blur-sm rounded-2xl border border-white/20 shadow-card-lg">
    <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8}
        d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
    </svg>
  </div>
);

const AuthLayout = ({ children }) => {
  const [lottieData, setLottieData] = useState(null);
  const lottieRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    const isValidLottie = (data) =>
      data && typeof data === 'object' && !Array.isArray(data) &&
      Array.isArray(data.layers) && typeof data.op === 'number';

    fetch('/animations/recruit.json')
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error('not found'))))
      .then((data) => {
        if (cancelled) return;
        if (isValidLottie(data)) setLottieData(data);
      })
      .catch(() => { /* cinematic backdrop renders instead */ });

    const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
    const apply = () => {
      if (mq.matches && lottieRef.current?.pause) lottieRef.current.pause();
    };
    mq.addEventListener?.('change', apply);
    apply();

    return () => {
      cancelled = true;
      mq.removeEventListener?.('change', apply);
    };
  }, []);

  return (
    <div className="min-h-screen flex bg-auth-gradient lg:bg-none">
      {/* ─── Left cinematic panel (lg+) ─────────────────────────────────── */}
      <aside className="hidden lg:flex lg:w-1/2 bg-auth-gradient relative overflow-hidden flex-col justify-between p-12 text-white">
        {lottieData ? (
          <div className="absolute inset-0 flex items-center justify-center" aria-hidden="true">
            <Lottie
              lottieRef={lottieRef}
              animationData={lottieData}
              loop
              autoplay
              style={{ width: '100%', height: '100%', maxWidth: 560 }}
            />
          </div>
        ) : (
          <AuthBackdrop />
        )}

        {/* Brand header (foreground) */}
        <div className="relative z-10 flex items-center gap-3">
          <BrandMark />
          <div>
            <h1 className="text-xl font-bold tracking-tight">Assessment Portal</h1>
            <p className="text-primary-200 text-xs font-medium">BNCtech · Recruitment Platform</p>
          </div>
        </div>

        {/* Spacer pushes headline to bottom */}
        <div className="relative z-10 flex-1" />

        {/* Headline + features (foreground, bottom) */}
        <div className="relative z-10 animate-fade-in max-w-md">
          <h2 className="text-4xl font-bold leading-[1.1] tracking-tight mb-3">
            Streamline your{' '}
            <span className="bg-gradient-to-r from-sky-200 via-white to-sky-200 bg-clip-text text-transparent">
              recruitment
            </span>{' '}
            workflow.
          </h2>
          <p className="text-primary-200 text-base mb-6">
            Smart assessments, real-time grading, and instant insights — all in one place.
          </p>
          <ul className="space-y-2.5 text-primary-100 text-sm">
            <li className="flex items-center gap-3"><CheckIcon /> Adaptive, secure online exams</li>
            <li className="flex items-center gap-3"><CheckIcon /> Auto-graded results in seconds</li>
            <li className="flex items-center gap-3"><CheckIcon /> Rich analytics for every cohort</li>
          </ul>
          <p className="text-primary-300 text-xs mt-8">© BNC Tech. All rights reserved.</p>
        </div>
      </aside>

      {/* ─── Right form panel ───────────────────────────────────────────── */}
      <main className="w-full lg:w-1/2 flex items-center justify-center p-4 sm:p-8 lg:bg-white relative overflow-hidden">
        {/* Mobile-only decorative blobs (preserves today's look on small screens) */}
        <div className="lg:hidden absolute top-0 left-0 w-96 h-96 bg-white/5 rounded-full -translate-x-1/2 -translate-y-1/2 pointer-events-none" />
        <div className="lg:hidden absolute bottom-0 right-0 w-[32rem] h-[32rem] bg-white/5 rounded-full translate-x-1/3 translate-y-1/3 pointer-events-none" />

        <div className="relative w-full max-w-md animate-scale-in">
          {/* Mobile-only brand header */}
          <div className="text-center mb-8 lg:hidden">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-white/15 backdrop-blur-sm rounded-2xl mb-4 border border-white/20 shadow-card-lg">
              <svg className="w-9 h-9 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8}
                  d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
              </svg>
            </div>
            <h1 className="text-2xl font-bold text-white tracking-tight">Assessment Portal</h1>
            <p className="text-primary-200 text-sm mt-1 font-medium">BNCtech · Recruitment Platform</p>
          </div>

          {/* Form card: white card on mobile (today's look), bare on lg+ */}
          <div className="bg-white rounded-3xl shadow-card-lg p-8 lg:bg-transparent lg:shadow-none lg:rounded-none lg:p-0">
            {children}
          </div>

          <p className="text-center text-primary-300 text-xs mt-6 lg:hidden">
            BNC Tech. All rights reserved.
          </p>
        </div>
      </main>
    </div>
  );
};

export default AuthLayout;
